import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import { fromEvent, merge, Subscription } from 'rxjs';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { SelectionModel } from '@angular/cdk/collections';
import { FormBuilder, FormGroup } from '@angular/forms';
import { select, Store } from '@ngrx/store';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { debounceTime, distinctUntilChanged, tap } from 'rxjs/operators';
import {
  AffiliationType,
  AffiliationTypesDataSource,
  AffiliationTypesPageRequested,
  selectAffiliationTypesActionLoading,
} from '../../../../../../../core/lists';
import { AppState } from '../../../../../../../core/reducers';
import {
  LayoutUtilsService,
  MessageType,
  QueryParamsModel,
} from '../../../../../../../core/_base/crud';
import { AffiliationTypeEditComponent } from '../affiliation-type-edit/affiliation-type-edit.component';
import { AffiliationTypeOnServerDeleted } from '../../../../../../../core/lists/_actions/affiliation-type.actions';
import { selectAffiliationTypeErrorState } from '../../../../../../../core/lists/_selectors/affiliation-type.selectors';

@Component({
  selector: 'kt-affiliation-types-list',
  templateUrl: './affiliation-types-list.component.html',
})
export class AffiliationTypeListComponent implements OnInit, OnDestroy {
  @Input() entityId: number;
  submissionErrorObj: any;
  hasSubmissionErrors: boolean;
  dataSource: AffiliationTypesDataSource;
  displayedColumns = ['affiliation_type_name', 'affiliation_type_description', 'actions'];
  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator;
  @ViewChild(MatSort, { static: true }) sort: MatSort;
  selection = new SelectionModel<AffiliationType>(true, []);
  affiliationTypeResult: AffiliationType[] = [];
  @ViewChild('searchInput', { static: true }) searchInput: ElementRef;
  loadingAfterSubmit = false;
  formGroup: FormGroup;

  private componentSubscriptions: Subscription;
  private subscriptions: any[] = [];
  private readonly viewContext;
  public readOnly: boolean;

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    public dialog: MatDialog,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    private layoutUtilsService: LayoutUtilsService,
    private activatedRoute: ActivatedRoute,
  ) {
    if (this.activatedRoute.snapshot.queryParams.viewContext) {
      this.viewContext = this.activatedRoute.snapshot.queryParams.viewContext;
    }
  }

  ngOnInit() {
    // If the user changes the sort order, reset back to the first page.
    const sortSubscription = this.sort.sortChange.subscribe(() => (this.paginator.pageIndex = 0));
    this.subscriptions.push({ id: 1, sub: sortSubscription });

    /* Data load will be triggered in two cases:
    - when a pagination event occurs => this.paginator.page
    - when a sort event occurs => this.sort.sortChange
    **/
    const paginatorSubscriptions = merge(this.sort.sortChange, this.paginator.page)
      .pipe(tap(() => this.loadAffiliationTypesList()))
      .subscribe();
    this.subscriptions.push({ id: 2, sub: paginatorSubscriptions });

    // this.subscriptions.push(this.sort.sortChange.subscribe(() => {
    //   this.paginator.pageIndex = 0
    //   this.dataSource.setAllItems(this.dataSource.getAllItems())
    // }));
    // Filtration, bind to searchInput
    const searchSubscription = fromEvent(this.searchInput.nativeElement, 'keyup')
      .pipe(
        debounceTime(150),
        distinctUntilChanged(),
        tap(() => {
          this.paginator.pageIndex = 0;
          this.loadAffiliationTypesList();
        }),
      )
      .subscribe();
    this.subscriptions.push({ id: 3, sub: searchSubscription });
    this.subscriptions.push(
      merge(this.sort.sortChange, this.paginator.page)
        .pipe(
          tap(() => {
            // tslint:disable-next-line:max-line-length
            const page = this.dataSource
              .getAllItems()
              .slice(
                this.paginator.pageIndex * this.paginator.pageSize,
                Math.min(
                  this.dataSource.getAllItems().length,
                  (this.paginator.pageIndex + 1) * this.paginator.pageSize,
                ),
              );
            this.dataSource.entitySubject.next(page);
          }),
        )
        .subscribe(),
    );

    // Init DataSource
    this.dataSource = new AffiliationTypesDataSource(this.store, this.paginator);
    this.subscriptions.push(
      this.dataSource.entitySubject.subscribe((res) => (this.affiliationTypeResult = res)),
    );
    this.loadAffiliationTypesList();
  }

  ngOnDestroy() {
    if (this.componentSubscriptions) {
      this.componentSubscriptions.unsubscribe();
    }
  }

  loadAffiliationTypesList() {
    this.selection.clear();
    const queryParams = new QueryParamsModel(
      this.filterConfiguration(),
      this.sort.direction !== '' ? this.sort.direction : 'desc',
      !this.sort.active || this.sort.active === '' ? 'created' : this.sort.active,
      this.paginator.pageIndex,
      this.paginator.pageSize,
    );
    // Call request from server
    this.store.dispatch(
      new AffiliationTypesPageRequested({
        page: queryParams,
      }),
    );
  }

  filterConfiguration(): any {
    const filter: any = {};
    const searchText: string = this.searchInput.nativeElement.value;
    filter.searchData = searchText;
    return filter;
  }

  addAffiliationType() {
    const newAffiliationType = new AffiliationType();
    newAffiliationType.clear(); // Set all defaults fields
    this.updateAffiliationType(newAffiliationType);
  }
  updateAffiliationType(affiliationType: AffiliationType) {
    let saveMessageTranslateParam = 'AFFILIATION.EDIT_AFFILIATION_TYPE_MS.';
    saveMessageTranslateParam += affiliationType.id > 0 ? 'UPDATE_MESSAGE' : 'ADD_MESSAGE';
    const _saveMessage = this.translate.instant(saveMessageTranslateParam);
    const _messageType = affiliationType.id > 0 ? MessageType.Update : MessageType.Create;
    const dialogRef = this.dialog.open(AffiliationTypeEditComponent, {
      data: {
        affiliationType,
        entityId: this.entityId,
        viewContext: this.viewContext,
      },
    });
    dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }
      this.layoutUtilsService.showActionNotification(_saveMessage, _messageType, 3000, true, false);
      this.loadAffiliationTypesList();
    });
  }
  deleteAffiliationType(_item: AffiliationType) {
    const _title: string = this.translate.instant(
      'AFFILIATION.DELETE_AFFILIATION_TYPE_SIMPLE.TITLE',
    );
    const _description: string = this.translate.instant(
      'AFFILIATION.DELETE_AFFILIATION_TYPE_SIMPLE.DESCRIPTION',
    );
    const _waitDesciption: string = this.translate.instant(
      'AFFILIATION.DELETE_AFFILIATION_TYPE_SIMPLE.WAIT_DESCRIPTION',
    );
    const _deleteMessage = this.translate.instant(
      'AFFILIATION.DELETE_AFFILIATION_TYPE_SIMPLE.MESSAGE',
    );

    const dialogRef = this.layoutUtilsService.deleteElement(_title, _description, _waitDesciption);
    const dialogRefSubscription = dialogRef.afterClosed().subscribe((res) => {
      if (!res) {
        return;
      }

      this.store.dispatch(new AffiliationTypeOnServerDeleted({ id: _item.affiliation_type_id }));
      const actionLoadingSubscription = this.store
        .pipe(select(selectAffiliationTypesActionLoading))
        .subscribe((response) => {
          if (response !== true) {
            const errorStateSubscription = this.store
              .pipe(select(selectAffiliationTypeErrorState))
              .subscribe((error) => {
                if (error) {
                  if (error.error && error.error.errors) {
                    this.submissionErrorObj = error.error.errors;
                  } else {
                    this.submissionErrorObj = [{ error: 'GLOBAL.UNSPECIFIED_ERROR' }];
                  }
                  this.hasSubmissionErrors = true;
                } else {
                  this.submissionErrorObj = null;
                  this.hasSubmissionErrors = false;
                }
              });
            if (this.subscriptions.find((sub) => sub.id === 9) === undefined) {
              this.subscriptions.push({ id: 9, sub: errorStateSubscription });
            }
            if (this.submissionErrorObj !== null) {
              const errArray = [];
              this.submissionErrorObj.forEach((errObj) => {
                const message = this.translate.instant(errObj.error);
                errArray.push(message);
              });
              const errMsg = errArray.join('\n');
              this.layoutUtilsService.showActionNotification(
                errMsg,
                MessageType.Delete,
                3000,
                true,
                false,
              );
            } else {
              this.layoutUtilsService.showActionNotification(
                _deleteMessage,
                MessageType.Delete,
                3000,
                true,
                false,
              );
              this.loadAffiliationTypesList();
            }
          }
          this.cdr.detectChanges();
        });
      if (this.subscriptions.find((sub) => sub.id === 8) === undefined) {
        this.subscriptions.push({ id: 8, sub: actionLoadingSubscription });
      }
    });
    if (this.subscriptions.find((sub) => sub.id === 7) === undefined) {
      this.subscriptions.push({ id: 7, sub: dialogRefSubscription });
    }
  }
}
